import {
    useCallback,
    useMemo,
    useRef,
    useState,
    type ComponentProps,
    type FC,
    type MouseEventHandler,
    type ReactNode,
} from 'react';
import { useTab } from 'dibs-elements/exports/useTab.ts';
import { eventNameConstants, stepInteractionConstants, trackEvent } from 'dibs-tracking';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';

//components
import { TabList } from 'dibs-elements/exports/TabList.tsx';
import { TabPanels } from 'dibs-elements/exports/TabPanels.tsx';
import { TabProvider } from 'dibs-elements/exports/TabProvider.ts';
import { HpSharedHeader } from '../HpSharedHeader/HpSharedHeader.tsx';
import { Chip } from 'dibs-elements/exports/Chip.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { HpSharedViewMoreLink } from '../HpSharedViewMoreLink/HpSharedViewMoreLink.tsx';

//styles
import dibsCss from 'dibs-css';

export type HpCarouselTab = {
    value: string;
    label: string;
    trigger: string;
    viewMoreLink: string;
};

const CustomTab: FC<{
    tab: HpCarouselTab;
    isLast?: boolean;
    scrollIntoViewOnClick?: boolean;
}> = ({ tab, isLast, scrollIntoViewOnClick }) => {
    const { handleClick, isActive } = useTab(tab.value);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const onClick: MouseEventHandler = useCallback(
        event => {
            handleClick();
            trackEvent(
                {
                    eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                    interaction_type: 'Pill Clicked',
                    trigger: tab.trigger,
                },
                event
            );
            if (scrollIntoViewOnClick && wrapperRef.current) {
                scrollIntoView(wrapperRef.current, {
                    time: 300,
                    align: { lockY: true },
                });
            }
        },
        [handleClick, tab.trigger, scrollIntoViewOnClick]
    );

    return (
        <div ref={wrapperRef} className={isLast ? undefined : dibsCss.mrXsmall}>
            <Chip onClick={onClick} isActive={isActive} dataTn={`hp-carousel-tabs-${tab.value}`}>
                {tab.label}
            </Chip>
        </div>
    );
};

type HpSharedCarouselTabsProps<T extends string = string> = {
    tabList: readonly T[];
    tabMap: Record<T, HpCarouselTab>;
    title: ReactNode;
    isMobile: boolean;
    renderPanel: (tabName: T) => ReactNode;
    defaultActiveTab?: T;
    renderOnInactive?: boolean;
};

export const HpSharedCarouselTabs = <T extends string>({
    tabList,
    tabMap,
    title,
    isMobile,
    renderPanel,
    defaultActiveTab,
    renderOnInactive = true,
}: HpSharedCarouselTabsProps<T>): ReactNode => {
    const [activeTab, setActiveTab] = useState(defaultActiveTab ?? tabList[0]);
    const activeDataTn = useMemo(
        () => `hp-carousel-tabs-${tabMap[activeTab].value}`,
        [activeTab, tabMap]
    );

    const handleViewMoreClick: ComponentProps<typeof Link>['onClick'] = useCallback(() => {
        trackEvent({
            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
            interaction_type: tabMap[activeTab].trigger,
            step_interaction_name:
                stepInteractionConstants.STEP_INTERACTION_RECOMMENDATION_VIEW_ALL_CLICK,
            trigger: tabMap[activeTab].trigger,
        });
    }, [tabMap, activeTab]);

    return (
        <>
            <HpSharedHeader
                title={title}
                subtitle={isMobile ? undefined : tabMap[activeTab].label}
                dataTn={activeDataTn}
                size="larger"
                align={isMobile ? 'left' : 'center'}
            />

            <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        isMobile ? dibsCss.gapMedium : dibsCss.gapLarge
                    )}
                >
                    {isMobile ? (
                        <div className="primary-viewport-width">
                            <TabList
                                fullWidth={false}
                                customTab
                                indicatorPadding="none"
                                underline={false}
                                indicatorHeight="none"
                                align="left"
                                overflowContainer
                                tabSpacing="xSmall"
                                listClassName={classnames(dibsCss.wMax, dibsCss.pxSmall)}
                            >
                                {tabList.map((tabName, index) => [
                                    tabName,
                                    <CustomTab
                                        key={tabName}
                                        tab={tabMap[tabName]}
                                        isLast={index === tabList.length - 1}
                                        scrollIntoViewOnClick={isMobile}
                                    />,
                                ])}
                            </TabList>
                        </div>
                    ) : (
                        <TabList
                            fullWidth
                            customTab
                            indicatorPadding="none"
                            underline={false}
                            indicatorHeight="none"
                            align="center"
                            overflowContainer={false}
                            tabSpacing="xSmall"
                        >
                            {tabList.map(tabName => [
                                tabName,
                                <CustomTab key={tabName} tab={tabMap[tabName]} />,
                            ])}
                        </TabList>
                    )}
                    <div className={classnames(dibsCss.flex, dibsCss.flexCol)}>
                        <TabPanels renderOnInactive={renderOnInactive}>
                            {tabList.map(tabName => [tabName, renderPanel(tabName)])}
                        </TabPanels>

                        <div className={classnames(isMobile ? dibsCss.selfStart : dibsCss.selfEnd)}>
                            <HpSharedViewMoreLink
                                viewMoreLink={tabMap[activeTab].viewMoreLink}
                                onViewMoreClick={handleViewMoreClick}
                                size="larger"
                                weight="heavy"
                                dataTn={activeDataTn}
                            />
                        </div>
                    </div>
                </div>
            </TabProvider>
        </>
    );
};
